import type { AccountSettingsForm } from '../AccountSettings';

export interface AccountSettingsData {
  firstname?: string;
  lastname?: string;
  job_position?: string;
  signature_job_position?: string;
  current_password?: string;
  new_password?: string;
  new_password_compare?: string;
}

export type AccountSettingsFormData = Partial<
  Record<AccountSettingsForm, string>
>;

export const PASSWORD_FIELDS_REQUIRED_ERROR =
  'Enter your current password, a new password, and repeat the new password.';
export const PASSWORDS_DONT_MATCH_ERROR = 'Passwords do not match.';
export const PASSWORD_COMPLEXITY_ERROR =
  'The new password must contain at least 8 characters, 1 uppercase letter, 1 number, and 1 special character.';

const passwordPattern = /^(?=.*[0-9])(?=.*[\W])(?=.*[A-Z])[a-zA-Z0-9\W]{8,}$/;

const apiFieldNames: Record<AccountSettingsForm, keyof AccountSettingsData> = {
  firstname: 'firstname',
  lastname: 'lastname',
  'job-position': 'job_position',
  'signature-job-position': 'signature_job_position',
  password: 'current_password',
  'new-password': 'new_password',
  'repeat-password': 'new_password_compare',
};

export const buildAccountSettingsData = (
  fields: AccountSettingsForm[],
  data: AccountSettingsFormData
): AccountSettingsData =>
  fields.reduce<AccountSettingsData>((payload, field) => {
    payload[apiFieldNames[field]] = data[field]?.toString() ?? '';
    return payload;
  }, {});

export const getPasswordSettingsError = (
  data: AccountSettingsFormData
): string | undefined => {
  const currentPassword = data.password?.toString() ?? '';
  const newPassword = data['new-password']?.toString() ?? '';
  const comparedPassword = data['repeat-password']?.toString() ?? '';

  if (!currentPassword || !newPassword || !comparedPassword)
    return PASSWORD_FIELDS_REQUIRED_ERROR;

  if (newPassword !== comparedPassword) return PASSWORDS_DONT_MATCH_ERROR;

  if (!passwordPattern.test(newPassword)) return PASSWORD_COMPLEXITY_ERROR;

  return undefined;
};

type ApiErrorPayload = {
  message?: unknown;
  errors?: unknown;
  data?: { errors?: unknown };
};

type AccountSettingsNetworkError = {
  statusCode?: number;
  response?: { status?: number };
  result?: ApiErrorPayload;
};

const validationMessages = (errors: unknown): string[] => {
  if (!errors || typeof errors !== 'object') return [];

  return Object.values(errors as Record<string, unknown>).flatMap((message) =>
    Array.isArray(message) ? message.map(String) : [String(message)]
  );
};

export const getAccountSettingsErrorMessage = (
  error: unknown,
  isPasswordSettings: boolean
): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;

  const networkError = (error as { networkError?: AccountSettingsNetworkError })
    .networkError;
  const result = networkError?.result;
  const messages = validationMessages(result?.errors ?? result?.data?.errors);

  if (messages.length) return messages.join(' ');

  const status = networkError?.statusCode ?? networkError?.response?.status;
  if (isPasswordSettings && status === 400)
    return 'The current password is incorrect, or the new passwords do not match.';

  if (typeof result?.message === 'string' && result.message)
    return result.message;

  return isPasswordSettings
    ? 'The password could not be changed. Please try again.'
    : 'Your settings could not be saved. Please try again.';
};
